import React from "react";
import styled from "styled-components";
import Link from "next/link";
import Image from "next/image";
import { IWGameItem } from "types/games";
import useGetWGames from "hooks/games/useGetWGames";
import useEmblaCarousel from "embla-carousel-react";
import WrewardsButton from "components/wrewards-ui-kit/Button";
import Arrow from "/public/images/icons/arrow-left.svg";
import { usePrevNextButtons } from "components/leaderboard/SlideButtonGroup";
import WSvg from "/public/images/NewAllGames/icons/w.svg";
import RevealSection from "components/common/RevealSection";
import useRevealGate from "hooks/useRevealGate";
import { useAppContext } from "contexts/AppContext";

export interface IWGamesComponent {
  id?: string;
}

const colors = {
  coinflip: "#5DAC01",
  crash: "#DB01FF",
  blackjack: "#FFC701",
  roulette: "#25FF01",
  mines: "#FF2F01",
  plinko: "#0171AC",
};

export const WGamesItemCard = ({ link, imageSrc, title, disabled }: IWGameItem) => {
  // @ts-ignore
  const specifiedColor = colors?.[title];
  return (
    <WGameCardContainer>
      {disabled && (
        <ComingBanner>
          <Image
            alt="lock"
            src="/images/games/lock.svg"
            width={14}
            height={14}
            style={{ marginRight: 8 }}
          />
          <span>coming soon</span>
        </ComingBanner>
      )}
      <WGameCard href={link} img={imageSrc} fcolor={specifiedColor} data-disabled={disabled}>
        <WGameTitle>
          {title}
          <span>w Originals</span>
        </WGameTitle>
      </WGameCard>
    </WGameCardContainer>
  );
};

export const WGamesOriginItems = () => {
  const { WGames } = useGetWGames();
  const { isFeatureFlagsReady } = useAppContext();
  const { isReady } = useRevealGate({
    preload: WGames.map((game) => game.imageSrc),
    isDataReady: isFeatureFlagsReady,
  });
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "center" });
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  return (
    <div>
      <ComponentTitleBlock>
        <ComponentTitle>
          <WSvg />
          WRewards Originals
        </ComponentTitle>
        <div style={{ display: "flex", gap: "16px" }}>
          <div style={{ display: "flex", gap: "8px" }}>
            <WrewardsButton
              variant="primary-icon"
              disabled={prevBtnDisabled}
              onClick={() => onPrevButtonClick()}
            >
              <Arrow />
            </WrewardsButton>
            <WrewardsButton
              variant="primary-icon"
              disabled={nextBtnDisabled}
              onClick={() => onNextButtonClick()}
            >
              <Arrow style={{ transform: "rotate(180deg)" }} />
            </WrewardsButton>
          </div>
        </div>
      </ComponentTitleBlock>

      <RevealSection isReady={isReady}>
        <CarouselWrapper>
          <div className="buttonSlider__viewport" ref={emblaRef}>
            <div className="buttonSlider__container">
              {WGames.map((item, index) => {
                return (
                  <WGamesItemCard
                    key={index}
                    link={item.link}
                    imageSrc={item.imageSrc}
                    title={item.title}
                    disabled={item.disabled}
                  />
                );
              })}
            </div>
          </div>
        </CarouselWrapper>
      </RevealSection>
    </div>
  );
};

const ComponentTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  margin: 0;
  svg {
    width: 24px;
    height: 24px;
    color: var(--pure-sky);
  }
`;

const ComponentTitleBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  font-family: var(--font-family-golos);
  font-weight: 600;
  color: var(--daylight-color);
`;

const CarouselWrapper = styled.div`
  --slide-spacing: 1rem;
  --slide-size: 170px;
  width: 100%;
  flex: 1;
  width: 100%;
  position: relative;
  @media (max-width: 940px) {
    max-width: 100%;
  }

  .buttonSlider__viewport {
    overflow: hidden;
    position: relative;
    z-index: 1;
    width: 100%;
  }
  .buttonSlider__container {
    width: 100%;
    display: flex;
    gap: 16px;
    touch-action: pan-y pinch-zoom;
  }
  .buttonSlider__slide {
    transform: translate3d(0, 0, 0);
    flex: 0 0 var(--slide-size);
    min-width: 100px;
    position: relative;
    z-index: 10;
  }
`;

export const ComingBanner = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0em;
  text-transform: uppercase;
  color: #e9f4ff;
  position: absolute;
  background: #1316204d;
  border-radius: 4px;
  z-index: 2;
  padding: 14px 12px;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WGameTitle = styled.div`
  display: flex;
  flex-direction: column;
  color: #ffffff;
  text-align: center;
  font-family: var(--font-family-exo);
  font-size: 22px;
  font-style: italic;
  font-weight: 700;
  letter-spacing: 0.3px;
  opacity: 0.9;
  text-transform: uppercase;
  gap: 10px;
  transition:
    gap 0.3s,
    font-size 0.3s;

  span {
    font-size: 10px;
    opacity: 0.75;
  }

  @media (max-width: 939px) {
    font-size: 20px;
    gap: 5px;
  }
`;
const WGameCardContainer = styled.div`
  position: relative;
  min-width: 170px;
  height: 100%;
`;
const WGameCard = styled(Link)<{ img: string; fcolor: string }>`
  overflow: hidden;
  display: flex;
  align-items: end;
  justify-content: center;
  flex: 1;
  position: relative;
  border-radius: 12px;
  padding: 16px;
  transition:
    padding 0.3s,
    box-shadow 0.2s;
  aspect-ratio: 3/4;
  /* color: attr(data-color); */

  &[data-disabled="true"] {
    filter: blur(3px);
    pointer-events: none;
  }

  &:before {
    content: "";
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(${(props) => props.img}) no-repeat center top / cover;
    transform: scale(1);
    transition: transform 0.3s;
  }
  box-shadow: 0px 0px 0px 0px ${(props) => props.fcolor};

  &:hover {
    padding: 10px 16px;
    box-shadow: 0px 0px 16px 2px ${(props) => props.fcolor};

    &:before {
      transform: scale(1.05);
    }

    ${WGameTitle} {
      gap: 0;
      font-size: 26px;

      @media (max-width: 939px) {
        font-size: 22px;
      }
    }
  }
`;
